import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../albumColors';
import { stickerCode, TOTAL, countsFromBase64 } from '../albumData';
import {
  parseFiguritasAppMessage,
  applyImportToBytes,
  summarizeImport,
} from '../utils/figuritasAppParser';

/**
 * Modal de import de álbum. Etapas:
 *
 *  1. 'source':     elegís de dónde importar (por ahora sólo figuritas.app).
 *  2. 'paste':      pegás el mensaje, ves preview, click "Importar".
 *  3. 'cc-confirm': si CC no apareció en el mensaje, preguntamos si tiene
 *                   todas o ninguna.
 *
 * Reglas (delegadas a applyImportToBytes):
 *  - País / FWC NO mencionado → user tiene todas las de esa sección.
 *  - CC NO mencionada → user elige "todas" o "ninguna".
 *  - Sección mencionada → todo lo no listado en "I need" = poseído;
 *    los listados en "Swaps" suman repe.
 */
function ImportAlbumDialog({ open, currentBase64, onClose, onConfirm, onToast }) {
  const { t } = useTranslation();
  const [stage, setStage] = useState('source'); // 'source' | 'paste' | 'cc-confirm'
  const [text, setText] = useState('');

  // Reset al cerrar / reabrir.
  useEffect(() => {
    if (open) {
      setStage('source');
      setText('');
    }
  }, [open]);

  const parsed = useMemo(() => parseFiguritasAppMessage(text), [text]);
  const ccMentioned = parsed.mentionedPrefixes.has('CC');

  // Resumen sin asumir CC (preserve por default → 0 CC contribución si no
  // está mencionada). Para el preview es OK mostrar este número y
  // ajustarlo si después el usuario elige todas/ninguna en cc-confirm.
  const summary = useMemo(() => summarizeImport(parsed), [parsed]);

  const conflicts = useMemo(() => {
    if (parsed.mentionedPrefixes.size === 0) return [];
    const current = currentBase64
      ? countsFromBase64(currentBase64)
      : new Uint8Array(TOTAL);
    return applyImportToBytes(current, parsed).conflicts;
  }, [parsed, currentBase64]);

  const hasContent = parsed.mentionedPrefixes.size > 0;

  const finalize = (ccChoice) => {
    const current = currentBase64
      ? countsFromBase64(currentBase64)
      : new Uint8Array(TOTAL);
    const { nextBytes, conflicts: finalConflicts } = applyImportToBytes(
      current,
      parsed,
      { ccChoice },
    );
    const b64 = btoa(String.fromCharCode(...nextBytes));
    const finalSummary = summarizeImport(parsed, { ccChoice });
    onConfirm({
      base64: b64,
      willSet: finalSummary.ownedCount,
      conflicts: finalConflicts.length,
    });
  };

  const handleImportClick = () => {
    if (!hasContent) {
      onToast?.({ message: t('No hay nada para importar'), variant: 'error' });
      return;
    }
    if (!ccMentioned) {
      setStage('cc-confirm');
      return;
    }
    finalize('preserve'); // CC mencionada → ya quedó cubierta por el parseo
  };

  const handleClose = () => {
    setStage('source');
    setText('');
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
        style={{ backgroundColor: 'var(--md-sys-color-surface, #ffffff)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-outline-variant/30 shrink-0">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]" style={{ color: C.primary }}>
              file_download
            </span>
            <h2 className="text-sm font-bold text-on-surface">
              {stage === 'source'
                ? t('Importar álbum')
                : stage === 'cc-confirm'
                  ? t('Confirmar Coca-Cola')
                  : t('Importar de figuritas.app')}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded hover:bg-black/5"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-[18px] text-on-surface-variant">close</span>
          </button>
        </div>

        {/* ───────────── STAGE: source ───────────── */}
        {stage === 'source' && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <p className="text-xs text-on-surface-variant">
                {t('Elegí desde dónde querés importar tu álbum:')}
              </p>
              <button
                type="button"
                onClick={() => setStage('paste')}
                className="w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all hover:shadow-md text-left"
                style={{
                  borderColor: `${C.primary}55`,
                  backgroundColor: `${C.primary}08`,
                }}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: C.primary, color: '#fff' }}
                >
                  <span className="material-symbols-outlined text-[28px]">style</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-on-surface">figuritas.app</div>
                  <div className="text-[11px] text-on-surface-variant">
                    {t('Pegá el mensaje "Missing & swaps" desde la app')}
                  </div>
                </div>
                <span className="material-symbols-outlined text-on-surface-variant shrink-0">chevron_right</span>
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-outline-variant/30 shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-on-surface-variant hover:bg-black/5"
              >
                {t('Cancelar')}
              </button>
            </div>
          </>
        )}

        {/* ───────────── STAGE: paste ───────────── */}
        {stage === 'paste' && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              <div
                className="flex items-start gap-2 p-3 rounded-lg text-xs"
                style={{
                  backgroundColor: `${C.primary}0d`,
                  border: `1px solid ${C.primary}33`,
                  color: C.onSurface,
                }}
              >
                <span
                  className="material-symbols-outlined text-[18px] shrink-0 mt-0.5"
                  style={{ color: C.primary }}
                >
                  info
                </span>
                <p>
                  {t('En figuritas.app, elegí')}{' '}
                  <span className="font-bold">{t('Missing & swaps')}</span>{' '}
                  {t('y compartí el mensaje. Copialo y pegalo abajo.')}
                </p>
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('Pegá acá el mensaje de figuritas.app...')}
                className="w-full min-h-[180px] max-h-[280px] rounded-lg border p-3 text-xs font-mono resize-y outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.18))',
                  backgroundColor: 'var(--md-sys-color-surface-container-lowest, #fafafa)',
                  color: 'var(--md-sys-color-on-surface, #1a1a1a)',
                }}
                spellCheck={false}
              />

              {hasContent && (
                <div className="space-y-2">
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: '#16a34a14', border: '1px solid #16a34a33' }}
                  >
                    <span className="material-symbols-outlined text-[22px]" style={{ color: '#16a34a' }}>
                      task_alt
                    </span>
                    <div className="text-xs leading-tight">
                      <div className="font-bold text-on-surface tabular-nums">
                        {ccMentioned
                          ? t('Vas a marcar {{n}} figuritas como tuyas', { n: summary.ownedCount })
                          : t('Vas a marcar al menos {{n}} figuritas como tuyas', { n: summary.ownedCount })}
                      </div>
                      <div className="text-on-surface-variant">
                        {summary.swapCount > 0
                          ? t('{{n}} con repetida (swaps)', { n: summary.swapCount })
                          : t('Sin swaps')}
                      </div>
                    </div>
                  </div>

                  {conflicts.length > 0 && (
                    <div
                      className="flex items-start gap-2 p-3 rounded-lg text-xs"
                      style={{
                        backgroundColor: '#f59e0b14',
                        border: '1px solid #f59e0b66',
                        color: '#7c2d12',
                      }}
                    >
                      <span className="material-symbols-outlined text-[20px] shrink-0 mt-0.5" style={{ color: '#d97706' }}>
                        warning
                      </span>
                      <div className="space-y-1 min-w-0">
                        <p className="font-bold">
                          {t('{{n}} figuritas que tenés se van a marcar como faltantes', {
                            n: conflicts.length,
                          })}
                        </p>
                        <p className="text-[11px] font-mono break-words tabular-nums">
                          {conflicts.slice(0, 30).map(stickerCode).join(', ')}
                          {conflicts.length > 30 && ` +${conflicts.length - 30}`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {parsed.unrecognized.length > 0 && (
                <div
                  className="flex items-start gap-2 p-3 rounded-lg text-xs"
                  style={{
                    backgroundColor: '#64748b14',
                    border: '1px solid #64748b44',
                    color: '#334155',
                  }}
                >
                  <span className="material-symbols-outlined text-[18px] shrink-0 mt-0.5" style={{ color: '#64748b' }}>
                    help
                  </span>
                  <div className="space-y-1 min-w-0">
                    <p className="font-bold">
                      {t('No reconocí {{n}} líneas', { n: parsed.unrecognized.length })}
                    </p>
                    <p className="text-[11px] font-mono break-words opacity-80">
                      {parsed.unrecognized.slice(0, 5).join(' · ')}
                      {parsed.unrecognized.length > 5 && ' ...'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-outline-variant/30 shrink-0">
              <button
                type="button"
                onClick={() => setStage('source')}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-bold text-on-surface-variant hover:bg-black/5"
              >
                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                {t('Volver')}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-on-surface-variant hover:bg-black/5"
                >
                  {t('Cancelar')}
                </button>
                <button
                  type="button"
                  onClick={handleImportClick}
                  disabled={!hasContent}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: C.primary, color: '#fff' }}
                >
                  <span className="material-symbols-outlined text-[16px]">file_download</span>
                  {conflicts.length > 0 ? t('Importar igual') : t('Importar')}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ───────────── STAGE: cc-confirm ───────────── */}
        {stage === 'cc-confirm' && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <div
                className="flex items-start gap-3 p-4 rounded-lg"
                style={{ backgroundColor: '#f59e0b14', border: '1px solid #f59e0b55' }}
              >
                <span className="text-[28px] shrink-0">🥤</span>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-on-surface">
                    {t('La sección Coca-Cola no aparece en el mensaje')}
                  </p>
                  <p className="text-xs text-on-surface-variant">
                    {t('A veces figuritas.app la incluye y a veces no. Para poder importar bien, decinos:')}
                  </p>
                </div>
              </div>

              <p className="text-sm font-medium text-on-surface text-center pt-2">
                {t('De Coca-Cola, ¿tenés todas o no tenés ninguna?')}
              </p>

              <div className="grid grid-cols-1 mobile-md:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => finalize('all')}
                  className="flex items-center gap-3 p-3 rounded-xl border-2 transition-all hover:shadow-md text-left"
                  style={{ borderColor: '#16a34a55', backgroundColor: '#16a34a0d' }}
                >
                  <span className="material-symbols-outlined text-[24px] shrink-0" style={{ color: '#16a34a' }}>
                    check_circle
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-on-surface">{t('Tengo todas')}</div>
                    <div className="text-[11px] text-on-surface-variant">{t('14/14 CC')}</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => finalize('none')}
                  className="flex items-center gap-3 p-3 rounded-xl border-2 transition-all hover:shadow-md text-left"
                  style={{ borderColor: '#dc262655', backgroundColor: '#dc26260d' }}
                >
                  <span className="material-symbols-outlined text-[24px] shrink-0" style={{ color: '#dc2626' }}>
                    do_not_disturb_on
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-on-surface">{t('No tengo ninguna')}</div>
                    <div className="text-[11px] text-on-surface-variant">{t('0/14 CC')}</div>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-outline-variant/30 shrink-0">
              <button
                type="button"
                onClick={() => setStage('paste')}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-bold text-on-surface-variant hover:bg-black/5"
              >
                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                {t('Volver')}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-on-surface-variant hover:bg-black/5"
              >
                {t('Cancelar')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ImportAlbumDialog;
